import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './ClassDetail.css';

const classData = {
  vinyasa: {
    title: 'Vinyasa Yoga',
    subtitle: 'Flow gracefully from one posture to another while improving strength, endurance, and balance.',
    about: 'Vinyasa Yoga links breath with movement to create a smooth and energizing practice that improves coordination and stamina. The continuous movement helps quiet the mind and improves cardiovascular health.',
    benefits: ['Full body workout', 'Improves balance', 'Burns calories', 'Builds strength', 'Increases flexibility'],
    level: 'Intermediate',
    duration: '60 Minutes',
    img: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=1600&auto=format&fit=crop'
  },
  ashtanga: {
    title: 'Ashtanga Yoga',
    subtitle: 'A structured and dynamic yoga practice designed to improve discipline, flexibility, and physical endurance.',
    about: 'Ashtanga follows a set sequence of postures, building intense internal heat and detoxifying the muscles and organs. It is a highly athletic and physically demanding practice perfect for building stamina.',
    benefits: ['Strong Core', 'Better Focus', 'Increased Stamina', 'Muscle Strength', 'Weight Management'],
    level: 'Advanced',
    duration: '90 Minutes',
    img: 'https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?q=80&w=1600&auto=format&fit=crop'
  },
  power: {
    title: 'Power Yoga',
    subtitle: 'An energetic yoga practice that combines strength training with flexibility exercises.',
    about: 'Power Yoga is a fitness-based vinyasa practice. An offshoot of Ashtanga Yoga, it focuses heavily on building strength and endurance while synchronizing breath with brisk movement.',
    benefits: ['Weight Loss', 'Burns Calories', 'Tones Muscles', 'Improves Endurance'],
    level: 'Intermediate to Advanced',
    duration: '60 Minutes',
    img: 'https://images.unsplash.com/photo-1552581234-26160f608093?q=80&w=1600&auto=format&fit=crop'
  },
  yin: {
    title: 'Yin Yoga',
    subtitle: 'Relax deeply with slow-paced stretches that target connective tissues and improve flexibility.',
    about: 'Yin Yoga consists of a series of long-held, passive floor poses that mainly work the lower part of the body. It is a slow-paced style of yoga with postures that are held for longer periods of time.',
    benefits: ['Stress Relief', 'Joint Mobility', 'Better Sleep', 'Deep Relaxation'],
    level: 'All Levels',
    duration: '75 Minutes',
    img: 'https://images.unsplash.com/photo-1508672019048-805c876b67e2?q=80&w=1600&auto=format&fit=crop'
  },
  meditation: {
    title: 'Meditation',
    subtitle: 'Discover mindfulness through guided meditation and breathing exercises.',
    about: 'Our guided meditation sessions help you disconnect from daily stress, anchor your attention to the present, and build emotional resilience through focused breathing techniques.',
    benefits: ['Mental Clarity', 'Emotional Balance', 'Reduced Anxiety', 'Better Sleep'],
    level: 'All Levels',
    duration: '45 Minutes',
    img: 'https://images.unsplash.com/photo-1594381898411-846e7d193883?q=80&w=1600&auto=format&fit=crop'
  },
  kids: {
    title: 'Kids Yoga',
    subtitle: "Fun yoga sessions designed to improve children's balance, confidence, and concentration.",
    about: 'Kids Yoga uses playful poses, games, and engaging breathing exercises to help children develop body awareness and emotional regulation in a highly joyful and supportive environment.',
    benefits: ['Better Focus', 'Healthy Growth', 'Improved Balance', 'Creativity'],
    level: 'Beginner (Kids)',
    duration: '45 Minutes',
    img: 'https://images.unsplash.com/photo-1545389336-ea7f71b9c1d3?q=80&w=1600&auto=format&fit=crop'
  },
  womens: {
    title: "Women's Yoga",
    subtitle: "Special yoga sessions focused on women's health, hormonal balance, flexibility, and overall wellness.",
    about: 'A supportive practice celebrating womanhood. Focuses on poses that nurture the female body through various life stages, heavily focusing on pelvic stability and core mindfulness.',
    benefits: ['Hormonal Balance', 'Stress Relief', 'Flexibility', 'Better Energy'],
    level: 'All Levels',
    duration: '60 Minutes',
    img: 'https://images.unsplash.com/photo-1518609878373-06d740f60d8b?q=80&w=1600&auto=format&fit=crop'
  },
  senior: {
    title: 'Senior Yoga',
    subtitle: 'Gentle yoga designed for older adults to improve mobility, balance, and overall health.',
    about: 'Our Senior Yoga uses modified poses and optional prop support to keep joints safe. It focuses heavily on fall-prevention, breathing depth, and maintaining muscular tone safely.',
    benefits: ['Joint Health', 'Better Balance', 'Easy Movements', 'Relaxation'],
    level: 'Beginner (Senior)',
    duration: '45 Minutes',
    img: 'https://images.unsplash.com/photo-1512438248247-f0f2a5a8b7f0?q=80&w=1600&auto=format&fit=crop'
  },
  personal: {
    title: 'Private Training',
    subtitle: 'Personalized one-on-one yoga sessions tailored to your health goals and fitness level.',
    about: 'Experience the ultimate individualized attention. Our expert instructors tailor everything from physical alignment cues to session pace uniquely for your body and your wellness goals.',
    benefits: ['Personal Guidance', 'Flexible Timings', 'Customized Plans', 'Faster Progress'],
    level: 'All Levels',
    duration: '60-90 Minutes',
    img: 'https://images.unsplash.com/photo-1524863479829-916d8e77f114?q=80&w=1600&auto=format&fit=crop'
  },
  hatha: {
    title: 'Hatha Yoga',
    subtitle: 'A foundational yoga practice emphasizing slow movement, deep stretching, and breath control.',
    about: 'Hatha Yoga is a classic system that includes the practice of asanas (yoga postures) and pranayama (breathing exercises), bringing peace to the mind and body. Perfect for fundamental mobility.',
    benefits: ['Strong Foundation', 'Improved Flexibility', 'Mind-Body Connection', 'Stress Relief'],
    level: 'Beginner to Intermediate',
    duration: '60 Minutes',
    img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=1600&auto=format&fit=crop'
  }
};

const ClassDetail = () => {
  const { id } = useParams();
  const data = classData[id];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!data) {
    return (
      <div className="class-not-found">
        <h2>Class Not Found</h2>
        <Link to="/" className="btn-return">Return Home</Link>
      </div>
    );
  }

  return (
    <div className="class-detail-page">
      {/* Hero Section with explicit image tag so it scales properly instead of aggressive CSS cover crops */}
      <div className="class-hero-wrapper">
        <img src={data.img} alt={data.title} className="class-hero-img" />
        <div className="class-hero-overlay"></div>
        <motion.div 
          className="class-hero-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="class-badge">OUR CLASSES</span>
          <h1 className="class-hero-title">{data.title}</h1>
          <p className="class-hero-subtitle">{data.subtitle}</p>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="class-main-container">
        <div className="class-content-grid">
          
          <motion.div 
            className="class-left-content"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-heading">About This Class</h2>
            <p className="class-about-text">{data.about}</p>
            
            <h2 className="section-heading mt-4">Benefits of {data.title}</h2>
            <ul className="class-benefits-list">
              {data.benefits.map((benefit, i) => (
                <li key={i}><span className="check">✔</span> {benefit}</li>
              ))}
            </ul>
          </motion.div>

          {/* Right Sidebar nicely aligned to the top of the grid */}
          <motion.div 
            className="class-sidebar-wrapper"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="sidebar-card">
              <h3 className="sidebar-title">Class Details</h3>
              
              <div className="detail-row">
                <span className="detail-icon">📊</span>
                <div>
                  <span className="detail-label">Level</span>
                  <p className="detail-value">{data.level}</p>
                </div>
              </div>

              <div className="detail-row">
                <span className="detail-icon">⏱</span>
                <div>
                  <span className="detail-label">Duration</span>
                  <p className="detail-value">{data.duration}</p>
                </div>
              </div>

              <Link to="/appointment" className="btn-book-sidebar">Book This Class</Link>
            </div>
          </motion.div>
          
        </div>
      </div>
    </div>
  );
};

export default ClassDetail;
